import React, { useState, useEffect } from 'react'
import {
  DialogContent, DialogActions, Button, TextField,
  CircularProgress, Switch, Accordion, AccordionSummary,
  AccordionDetails, Typography,
  FormControlLabel, InputAdornment, MenuItem,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'

const useStyles = makeStyles((theme) => ({
  content: {
    padding: theme.spacing(2, 3),
    maxHeight: '78vh',
    overflowY: 'auto',
    [theme.breakpoints.down('xs')]: { padding: theme.spacing(1, 1.5) },
    '&::-webkit-scrollbar': { width: 6 },
    '&::-webkit-scrollbar-thumb': { background: '#dadce0', borderRadius: 3 },
  },
  panel: {
    border: '1px solid #dadce0',
    borderRadius: '8px !important',
    boxShadow: 'none',
    marginBottom: theme.spacing(1.5),
    '&:before': { display: 'none' },
    '&$expanded': { margin: `0 0 ${theme.spacing(1.5)}px` },
  },
  expanded: {},
  panelSummary: {
    minHeight: 56,
    '&$summaryExpanded': { minHeight: 56 },
  },
  summaryExpanded: {},
  panelTitle: { fontSize: 14, fontWeight: 500, color: '#202124' },
  panelHint: { fontSize: 12, color: '#5f6368', marginTop: 2 },
  panelDetails: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(2),
    padding: theme.spacing(1, 2, 2),
    [theme.breakpoints.down('xs')]: { gridTemplateColumns: 'minmax(0, 1fr)' },
  },
  fullWidth: { gridColumn: '1 / -1' },
  switchRow: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    flexWrap: 'wrap',
    gridColumn: '1 / -1',
  },
  actions: {
    padding: theme.spacing(1.5, 3, 2),
    gap: 8,
    justifyContent: 'flex-end',
    position: 'sticky',
    bottom: 0,
    background: '#fff',
    zIndex: 1,
    borderTop: '1px solid #e8eaed',
    [theme.breakpoints.down('xs')]: { padding: theme.spacing(1.5) },
  },
  saveBtn: {
    backgroundColor: '#1a73e8',
    color: '#fff',
    textTransform: 'none',
    fontWeight: 500,
    borderRadius: 20,
    padding: '7px 22px',
    boxShadow: 'none',
    '&:hover': { backgroundColor: '#1557b0', boxShadow: '0 1px 3px rgba(0,0,0,.2)' },
    '&:disabled': { backgroundColor: '#dadce0', color: '#5f6368', boxShadow: 'none' },
  },
  cancelBtn: {
    color: '#5f6368',
    textTransform: 'none',
    borderRadius: 20,
    padding: '7px 22px',
    '&:hover': { backgroundColor: '#f1f3f4' },
  },
}))

const EMPTY = {
  nom: '', id_parent: '',
  adresse: '', ville: '', pays: '', cp: '',
  telephone: '', fax: '',
  remise_n: 0, remise_sp: 0, echeance_n: 60, echeance_sp: 30,
  comptant: 0, id_tva: 1, representant: 0,
  code_ag: '', code_gu: '', compte: '', cle_rib: '',
  observations: '', position: '', affact: 0,
  compte_quadra: '01000000', compte_affacturage: '01000000',
  desc_pendentif: '', rib_aff: '', bic_aff: '', coef: 2.5,
}

export default function ClientForm({
  initial = {},
  agences = [],
  tvas = [],
  tvasLoading = false,
  tvasError = '',
  onRetryTVAs,
  onCancel,
  onSave,
  saving = false,
}) {
  const classes = useStyles()
  const [data, setData]     = useState(EMPTY)
  const [errors, setErrors] = useState({})
  const [expandedSection, setExpandedSection] = useState('profile')

  useEffect(() => {
    setData({ ...EMPTY, ...initial })
    setErrors({})
    setExpandedSection('profile')
  }, [initial])

  const set = (k) => (e) => {
    const val = e.target.type === 'checkbox' ? (e.target.checked ? 1 : 0) : e.target.value
    setData(d => ({ ...d, [k]: val }))
    if (errors[k]) setErrors(er => ({ ...er, [k]: undefined }))
  }

  const validateField = (key, value) => {
    if (key === 'nom' && !String(value ?? '').trim()) return 'Saisissez le nom du client.'
    if (['remise_n', 'remise_sp'].includes(key) && value !== '') {
      const number = Number(value)
      if (!Number.isFinite(number) || number < 0 || number > 100) return 'Saisissez un pourcentage entre 0 et 100.'
    }
    if (['echeance_n', 'echeance_sp'].includes(key) && value !== '') {
      const number = Number(value)
      if (!Number.isInteger(number) || number < 0) return 'Saisissez un nombre de jours supérieur ou égal à 0.'
    }
    if (key === 'coef' && value !== '') {
      const number = Number(value)
      if (!Number.isFinite(number) || number <= 0) return 'Le coefficient doit être supérieur à 0.'
    }
    if (key === 'id_tva' && tvas.length && !tvas.some(tva => String(tva.id_tva) === String(value))) {
      return 'Sélectionnez un taux de TVA dans la liste.'
    }
    return ''
  }

  const submit = (event) => {
    event.preventDefault()
    const nextErrors = ['nom', 'remise_n', 'remise_sp', 'echeance_n', 'echeance_sp', 'coef', 'id_tva']
      .reduce((result, key) => {
        const message = validateField(key, data[key])
        if (message) result[key] = message
        return result
      }, {})
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length) {
      const firstInvalidField = Object.keys(nextErrors)[0]
      setExpandedSection(['remise_n', 'remise_sp', 'echeance_n', 'echeance_sp', 'coef', 'id_tva'].includes(firstInvalidField)
        ? 'commercial'
        : 'profile')
      return
    }
    onSave && onSave({ ...data, nom: String(data.nom).trim() })
  }

  const f = (label, key, opts = {}) => (
    <TextField
      key={key}
      label={label}
      value={data[key] ?? ''}
      onChange={set(key)}
      variant="outlined"
      size="small"
      error={!!errors[key]}
      helperText={errors[key]}
      disabled={saving}
      onBlur={(event) => {
        const message = validateField(key, event.target.value)
        setErrors(current => ({ ...current, [key]: message || undefined }))
      }}
      {...opts}
    />
  )

  const sw = (label, key) => (
    <FormControlLabel
      key={key}
      label={label}
      control={
        <Switch
          checked={Boolean(Number(data[key]))}
          onChange={(e) => setData(d => ({ ...d, [key]: e.target.checked ? 1 : 0 }))}
          color="primary"
          size="small"
          disabled={saving}
        />
      }
    />
  )

  const parentOptions = agences.filter(a => String(a.id) !== String(initial.id))
  const hasSelectedTva = tvas.some(tva => String(tva.id_tva) === String(data.id_tva))
  const panel = (id, title, hint, children) => (
    <Accordion
      className={classes.panel}
      classes={{ expanded: classes.expanded }}
      expanded={expandedSection === id}
      onChange={() => setExpandedSection(current => current === id ? false : id)}
    >
      <AccordionSummary
        className={classes.panelSummary}
        classes={{ expanded: classes.summaryExpanded }}
        expandIcon={<ExpandMoreIcon />}
      >
        <div>
          <Typography className={classes.panelTitle}>{title}</Typography>
          <Typography className={classes.panelHint}>{hint}</Typography>
        </div>
      </AccordionSummary>
      <AccordionDetails className={classes.panelDetails}>
        {children}
      </AccordionDetails>
    </Accordion>
  )

  return (
    <form onSubmit={submit} noValidate>
      <DialogContent className={classes.content}>
        {panel('profile', 'Client', 'Identité, adresse et coordonnées', <>
          {f('Nom du client *', 'nom', { required: true, autoFocus: true })}
          <TextField
            select
            label="Agence parente"
            value={String(data.id_parent ?? '')}
            onChange={set('id_parent')}
            variant="outlined"
            size="small"
            disabled={saving}
          >
            <MenuItem value="">Aucune</MenuItem>
            {parentOptions.map(agence => (
              <MenuItem key={agence.id} value={String(agence.id)}>{agence.nom}</MenuItem>
            ))}
          </TextField>
          {f('Rue', 'adresse', { className: classes.fullWidth })}
          {f('Code postal', 'cp')}
          {f('Ville', 'ville')}
          {f('Pays', 'pays')}
          {f('Téléphone', 'telephone', { type: 'tel' })}
          {f('Fax', 'fax', { type: 'tel' })}
          {initial.code_agence != null && (
            <TextField
              label="Code agence"
              value={initial.code_agence}
              variant="outlined"
              size="small"
              disabled
            />
          )}
          <div className={classes.switchRow}>
            {sw('Représentant', 'representant')}
            {sw('Comptant', 'comptant')}
            {sw('Affacturage', 'affact')}
          </div>
        </>)}

        {panel('commercial', 'Conditions commerciales', 'TVA, remises, échéances et coefficient', <>
          <TextField
            select
            label="Taux de TVA"
            value={String(data.id_tva ?? '')}
            onChange={set('id_tva')}
            onBlur={() => setErrors(current => ({
              ...current,
              id_tva: validateField('id_tva', data.id_tva) || undefined,
            }))}
            variant="outlined"
            size="small"
            disabled={saving || tvasLoading || !!tvasError}
            error={!!errors.id_tva}
            helperText={errors.id_tva || (tvasLoading ? 'Chargement des taux…' : '')}
            required={tvas.length > 0}
          >
            {!hasSelectedTva && data.id_tva !== '' && (
              <MenuItem value={String(data.id_tva)} disabled>
                Taux actuel indisponible (ID {data.id_tva})
              </MenuItem>
            )}
            {tvas.map(tva => (
              <MenuItem key={tva.id_tva} value={String(tva.id_tva)}>
                {tva.description} — {tva.valeur} %
              </MenuItem>
            ))}
          </TextField>
          {tvasError && (
            <Button size="small" color="primary" onClick={onRetryTVAs} disabled={tvasLoading}>
              {tvasLoading ? 'Chargement…' : 'Recharger les taux de TVA'}
            </Button>
          )}
          {f('Remise N', 'remise_n', {
            type: 'number',
            inputProps: { min: 0, max: 100, step: 0.01 },
            InputProps: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
          })}
          {f('Remise SP', 'remise_sp', {
            type: 'number',
            inputProps: { min: 0, max: 100, step: 0.01 },
            InputProps: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
          })}
          {f('Échéance N', 'echeance_n', {
            type: 'number',
            inputProps: { min: 0, step: 1 },
            InputProps: { endAdornment: <InputAdornment position="end">jours</InputAdornment> },
          })}
          {f('Échéance SP', 'echeance_sp', {
            type: 'number',
            inputProps: { min: 0, step: 1 },
            InputProps: { endAdornment: <InputAdornment position="end">jours</InputAdornment> },
          })}
          {f('Coefficient', 'coef', {
            type: 'number',
            inputProps: { min: 0, step: 0.01 },
          })}
        </>)}

        {panel('accounting', 'Comptabilité et affacturage', 'Comptes et références de facturation', <>
          {f('Compte', 'compte')}
          {f('Clé RIB', 'cle_rib')}
          {f('Compte Quadra', 'compte_quadra')}
          {f('Compte d’affacturage', 'compte_affacturage')}
          {f('Code AG', 'code_ag')}
          {f('Code GU', 'code_gu')}
          {f('RIB d’affacturage', 'rib_aff')}
          {f('BIC d’affacturage', 'bic_aff')}
          {f('Description du pendentif', 'desc_pendentif')}
        </>)}

        {panel('notes', 'Notes', 'Informations complémentaires', <>
          {f('Observations', 'observations', {
            className: classes.fullWidth,
            multiline: true,
            rows: 4,
          })}
        </>)}
      </DialogContent>

      <DialogActions className={classes.actions}>
        <Button onClick={onCancel} disabled={saving} className={classes.cancelBtn}>
          Annuler
        </Button>
        <Button type="submit" disabled={saving} variant="contained" className={classes.saveBtn}
          startIcon={saving ? <CircularProgress size={15} color="inherit" /> : null}>
          {saving ? 'Enregistrement…' : 'Enregistrer le client'}
        </Button>
      </DialogActions>
    </form>
  )
}
